import { useCallback, useContext } from "react";

import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import LocationDao from "../../dao/LocationDao";
import UserDao from "../../dao/UserDao";

import { GlobalContext } from "./ContextProvider";
import { hasRole } from "../../utils/isUser";
import { SCHOOL_KEY_EXCEPTION } from "../../const/LocationConst";

/**
 * A collection of context setters
 */

/**
 * SchoolYearPerformance "getter" flag
 * @type {boolean}
 */
let getSY = true;
let getAllSY = true;

/**
 * TimePeriod "getter" flag
 * @type {boolean}
 */
let getTP = true;

/**
 * Current timePeriod "getter" flag
 * @type {boolean}
 */
let getCurrentTP = true;

/**
 * District Template "getter" flag
 * @type {boolean}
 */
let getDT = true;

/**
 * School Template "getter" flag
 * @type {boolean}
 */
let getST = true;

/**
 * Action Control "getter" flag
 * @type {boolean}
 */
let getACW = true;

/**
 * Get all locations and set it in context
 * @name SetAllLocationDtos
 * @constructor
 */
export const SetAllLocationDtos = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { allLocationDtos, token } = state || {};
    if (token && !allLocationDtos) {
        if (getAllSY) {
            getAllSY = false;
            const options = {
                action: "locationsSearchableRead",
                params: {
                    facetField: "locationType",
                    searchString: "locationType=ELEMENTARY_SCHOOL,ELEMENTARY_LEGACY",
                    sort: "name",
                    numRows: "1000"
                },
                token
            };
            LocationDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        if (payload) {
                            dispatch({
                                type: "AllLocationDtos",
                                allLocationDtos: payload.results.filter(
                                    (school) =>
                                        school.locationType === "ELEMENTARY_SCHOOL" ||
                                        String(school.key) === String(SCHOOL_KEY_EXCEPTION)
                                )
                            });
                            // Retired elementary schools are kept out of allLocationDtos so existing
                            // consumers keep seeing only open schools; historical views opt in to these.
                            dispatch({
                                type: "LegacyLocationDtos",
                                legacyLocationDtos: payload.results.filter(
                                    (school) => school.locationType === "ELEMENTARY_LEGACY"
                                )
                            });
                        }
                        getAllSY = true;
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }
};

/**
 * Get the schoolYearDto and set it in context
 * @name SetSchoolYearDto
 * @constructor
 */
export const SetSchoolYearDto = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { schoolYearDto, token } = state || {};

    if (token && !schoolYearDto) {
        if (getSY) {
            getSY = false;
            const options = {
                action: "activeSchoolYearRead",
                token
            };
            UserDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        if (Object.keys(payload).length) {
                            dispatch({
                                type: "SchoolYearDto",
                                schoolYearDto: payload
                            });
                        }
                        getSY = true;
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }
};

/**
 * Get the time periods and set it in context
 * @name SetTimePeriods
 * @constructor
 */
export const SetTimePeriodDto = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { schoolYearDto, timePeriodDto, token } = state || {};

    if (getTP && schoolYearDto && !timePeriodDto && token) {
        getTP = false;
        const options = {
            action: "getTimePeriodId",
            schoolYearKey: schoolYearDto.key,
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response) {
                    const { payload } = response.data;
                    dispatch({
                        type: "TimePeriodDto",
                        timePeriodDto: payload
                    });
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }
};

/**
 * Get the time periods and set it in context
 * @name SetTimePeriods
 * @constructor
 */
export const SetCurrentTimePeriodDto = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { schoolYearDto, currentTimePeriodDto, token } = state || {};

    if (getCurrentTP && schoolYearDto && !currentTimePeriodDto && token) {
        getCurrentTP = false;
        const options = {
            action: "getCurrentTimePeriodDto",
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response) {
                    const { payload } = response.data;
                    dispatch({
                        type: "CurrentTimePeriodDto",
                        currentTimePeriodDto: payload
                    });
                    if (!state.viewTimePeriodDto) {
                        dispatch({ type: "ViewTimePeriodDto", viewTimePeriodDto: payload });
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }
};

/**
 * Get the District Level Template and set it in context.
 * Always dispatches after the fetch completes — either the real template or null.
 * Dispatching null (rather than leaving state undefined) signals to downstream
 * consumers that the fetch is done and no template exists yet, preventing
 * infinite loading states that wait on a district template that may not exist.
 * @name SetDistrictTemplateDto
 * @constructor
 */
export const SetDistrictTemplateDto = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { schoolYearDto, districtTemplateDto, timePeriodDto, token, userDetails } = state || {};
    if (hasRole(userDetails, ["EPR_ADMIN", "PK12", "PRINCIPAL", "ASSISTANT_PRINCIPAL", "TEACHER"])) {
        if (getDT && schoolYearDto?.key && timePeriodDto && !districtTemplateDto && token) {
            getDT = false;
            const options = {
                action: "getTemplateByLocationAndTimePeriod",
                locationKey: 0,
                timePeriodId: timePeriodDto.find((period) => period.name === "Full Year").key,
                schoolYearKey: schoolYearDto.key,
                token
            };

            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response && response.data && response.data.payload?.profileId !== null) {
                        const { payload } = response.data;
                        dispatch({
                            type: "DistrictTemplateDto",
                            districtTemplateDto: payload
                        });
                    } else {
                        // No district template exists yet — dispatch null so downstream
                        // code knows the fetch completed rather than treating it as pending.
                        dispatch({
                            type: "DistrictTemplateDto",
                            districtTemplateDto: null
                        });
                    }
                })
                .catch((error) => {
                    console.error(error);
                    dispatch({
                        type: "DistrictTemplateDto",
                        districtTemplateDto: null
                    });
                });
        }
    }
};

/**
 * Get the School Level Template and set it in context
 * @name SetSchoolTemplateDto
 * @constructor
 */
export const SetSchoolTemplateDto = () => {
    const { dispatch, state } = useContext(GlobalContext);

    const { schoolYearDto, districtTemplateDto, timePeriodDto, schoolLevelTemplateDto, userDetails, token } =
        state || {};

    const locationKey = userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey;
    if (hasRole(userDetails, ["EPR_ADMIN", "PK12", "PRINCIPAL", "ASSISTANT_PRINCIPAL", "TEACHER"])) {
        // districtTemplateDto is null when confirmed absent, undefined while still loading.
        // If there's no district template, a school template can't exist either — dispatch null
        // so consumers know loading is done rather than waiting indefinitely.
        if (districtTemplateDto === null && schoolLevelTemplateDto === undefined) {
            dispatch({ type: "SchoolLevelTemplateDto", schoolLevelTemplateDto: null });
        }
        if (
            districtTemplateDto &&
            userDetails &&
            locationKey &&
            schoolYearDto?.key &&
            timePeriodDto &&
            token &&
            !schoolLevelTemplateDto
        ) {
            if (getST) {
                getST = false;
                const options = {
                    action: "getTemplateByLocationAndTimePeriod",
                    locationKey,
                    timePeriodId: timePeriodDto.find((period) => period.name === "1st Semester").key,
                    schoolYearKey: schoolYearDto.key,
                    token
                };
                ElementaryProgressReportDao(options)
                    .then((response) => {
                        if (response && response.data && response.data.payload?.profileId !== null) {
                            const { payload: semesterOnePayload } = response.data;
                            const options = {
                                action: "getTemplateByLocationAndTimePeriod",
                                locationKey,
                                timePeriodId: timePeriodDto.find((period) => period.name === "2nd Semester").key,
                                schoolYearKey: schoolYearDto.key,
                                token
                            };
                            ElementaryProgressReportDao(options).then((response) => {
                                if (response && response.data && response.data.payload?.profileId !== null) {
                                    const { payload: semesterTwoPayload } = response.data;
                                    dispatch({
                                        type: "SchoolLevelTemplateDto",
                                        schoolLevelTemplateDto: {
                                            firstSemester: semesterOnePayload,
                                            secondSemester: semesterTwoPayload
                                        }
                                    });
                                    dispatch({
                                        type: "SchoolTemplatesByLocationMerge",
                                        schoolTemplatesByLocation: {
                                            [locationKey]: {
                                                firstSemester: semesterOnePayload,
                                                secondSemester: semesterTwoPayload
                                            }
                                        }
                                    });
                                }
                            });
                        } else {
                            dispatch({
                                type: "SchoolLevelTemplateDto",
                                schoolLevelTemplateDto: null
                            });
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            }
        }
    }
};

/**
 * Ensure School Level Templates for a specific locationKey are loaded into cache.
 * Stores in state.schoolTemplatesByLocation[locationKey] = { firstSemester, secondSemester }
 */
export const useEnsureSchoolTemplatesForLocation = () => {
    const { dispatch, state } = useContext(GlobalContext);

    const { schoolYearDto, timePeriodDto, token, schoolTemplatesByLocation, schoolTemplatesLoadingByLocation } =
        state || {};

    const schoolYearKey = schoolYearDto?.key;

    return useCallback(
        ({ locationKey, forceRefetch = false }) => {
            if (!locationKey || !token || !schoolYearKey || !timePeriodDto) return;

            if (!forceRefetch) {
                if (schoolTemplatesByLocation?.[locationKey]) return;
                if (schoolTemplatesLoadingByLocation?.[locationKey]) return;
            }

            dispatch({
                type: "SchoolTemplatesLoadingByLocationMerge",
                schoolTemplatesLoadingByLocation: { [locationKey]: true }
            });

            const firstTp = timePeriodDto.find((p) => p.name === "1st Semester")?.key;
            const secondTp = timePeriodDto.find((p) => p.name === "2nd Semester")?.key;

            const fetchTemplate = (timePeriodId) =>
                ElementaryProgressReportDao({
                    action: "getTemplateByLocationAndTimePeriod",
                    locationKey,
                    timePeriodId,
                    schoolYearKey,
                    token
                });

            Promise.all([fetchTemplate(firstTp), fetchTemplate(secondTp)])
                .then(([sem1Res, sem2Res]) => {
                    const sem1 = sem1Res?.data?.payload?.profileId ? sem1Res.data.payload : null;
                    const sem2 = sem2Res?.data?.payload?.profileId ? sem2Res.data.payload : null;

                    dispatch({
                        type: "SchoolTemplatesByLocationMerge",
                        schoolTemplatesByLocation: {
                            [locationKey]: { firstSemester: sem1, secondSemester: sem2 }
                        }
                    });
                })
                .catch((error) => console.error(error))
                .finally(() => {
                    dispatch({
                        type: "SchoolTemplatesLoadingByLocationMerge",
                        schoolTemplatesLoadingByLocation: { [locationKey]: false }
                    });
                });
        },
        [dispatch, token, schoolYearKey, timePeriodDto, schoolTemplatesByLocation, schoolTemplatesLoadingByLocation]
    );
};

/**
 * Get the Action
 * @name SetActionControlWindos
 * @constructor
 */
export const SetActionWindows = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { actionWindowsDto, schoolYearDto, currentTimePeriodDto, token, userDetails } = state || {};
    if (hasRole(userDetails, ["EPR_ADMIN", "PK12", "PRINCIPAL", "ASSISTANT_PRINCIPAL", "TEACHER"])) {
        if (getACW && schoolYearDto?.key && currentTimePeriodDto && !actionWindowsDto && token) {
            getACW = false;
            const options = {
                action: "actionWindowsRead",
                locationKey: userDetails.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey,
                schoolYearKey: schoolYearDto.key,
                token
            };

            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response && response.data && response.data.payload?.profileId !== null) {
                        const { payload } = response.data;

                        dispatch({
                            type: "ActionWindowsDto",
                            actionWindowsDto: payload
                        });
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }
};
